import { useEffect, useMemo, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import type { Product, ProductVariant } from '@/data/products';
import { PRODUCTS_QUERY_KEY, type ProductListResponse, useProductQuery } from '@/data/products.queries';

type LocationState = { product?: Product } | null;

export function useProductDetail() {
  const params = useParams<{ slug?: string; id?: string }>();
  const slugParam = params.slug ?? params.id ?? null;
  const location = useLocation();
  const queryClient = useQueryClient();
  const [selectedVariant, setSelectedVariant] = useState<ProductVariant | null>(null);

  const initialProduct = (location.state as LocationState)?.product ?? null;
  const slug = slugParam ?? initialProduct?.slug ?? null;

  const cachedLists = queryClient.getQueriesData<ProductListResponse>({ queryKey: PRODUCTS_QUERY_KEY });
  const cachedProduct = slug
    ? cachedLists
        .flatMap(([, listResponse]) => listResponse?.products ?? [])
        .find((product) => product.slug === slug) ?? null
    : null;
  const seedProduct = initialProduct?.slug === slug ? initialProduct : cachedProduct;

  const productQuery = useProductQuery(slug, seedProduct);
  const product = productQuery.data ?? null;

  useEffect(() => {
    if (!product) {
      setSelectedVariant(null);
      return;
    }

    setSelectedVariant(product.variants?.[0] ?? null);
  }, [product?.id]);

  const activeVariant = useMemo(
    () =>
      selectedVariant?.id
        ? product?.variants?.find((variant) => variant.id === selectedVariant.id) ?? null
        : null,
    [product?.variants, selectedVariant?.id]
  );

  const images = useMemo(() => {
    if (activeVariant) {
      if (activeVariant.galleryPaths?.length) return activeVariant.galleryPaths;
      if (activeVariant.imagePath) return [activeVariant.imagePath];
      return [];
    }

    if (product?.gallery_paths?.length) return product.gallery_paths;
    if (product?.image_path) return [product.image_path];
    return [];
  }, [activeVariant, product?.gallery_paths, product?.image_path]);

  const loadError =
    productQuery.error instanceof Error
      ? productQuery.error.message
      : productQuery.error
      ? 'Failed to load product.'
      : null;

  return {
    slug,
    product,
    selectedVariant,
    setSelectedVariant,
    activeVariant,
    images,
    loadError,
    isLoading: productQuery.isLoading,
  };
}
